import { useEffect, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { jacketProducts } from '@/mocks/products';

function RevealDiv({ children, className, style }: { children: React.ReactNode; className?: string; style?: React.CSSProperties }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.classList.add('ew-revealed'); obs.unobserve(el); } }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return <div ref={ref} className={`ew-reveal ${className ?? ''}`} style={style}>{children}</div>;
}

const jackets = jacketProducts;

export default function Jackets() {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-20">
        {/* Hero */}
        <section className="relative py-28 flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img src="https://public.readdy.ai/ai/img_res/46fdf929851fa6a1bf21803b4436e52a.jpg" alt="Jackets hero" className="w-full h-full object-cover object-top" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/40 to-black/65"></div>
          </div>
          <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">
            <p style={{ fontFamily: '"Dancing Script", cursive', color: '#E8B96A', fontSize: '1.3rem' }} className="mb-2">Silk. Velvet. Beaded. Breathtaking.</p>
            <h1 style={{ fontFamily: '"Playfair Display", serif', color: '#fff' }} className="text-5xl md:text-6xl font-black mb-4">Goddess Jackets<br /><em style={{ color: '#E8B96A' }}>&amp; Ruanas</em></h1>
            <p style={{ fontFamily: '"Lato", sans-serif', color: '#EDE5D8', lineHeight: 1.8 }} className="text-lg mb-8">
              Plus-size and regular-size silk and velvet beaded jackets, ruanas, and designer jackets. Outrageously gorgeous options for every body type.
            </p>
            <a href="#jacket-grid" className="inline-flex items-center gap-2 px-8 py-4 rounded-lg font-bold uppercase tracking-wide whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5" style={{ backgroundColor: '#C9933A', color: '#fff', fontFamily: '"Lato", sans-serif' }}>
              Browse Jackets
            </a>
          </div>
        </section>

        {/* Plus-size banner */}
        <section style={{ backgroundColor: '#7A2E2E' }} className="py-6">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <p style={{ fontFamily: '"Lato", sans-serif', color: '#FAF7F2', fontWeight: 700 }} className="text-sm uppercase tracking-widest">
              <i className="ri-heart-3-fill mr-2" style={{ color: '#E8B96A' }}></i>
              All jackets available in regular &amp; plus sizes — because gorgeous has no size limit
              <i className="ri-heart-3-fill ml-2" style={{ color: '#E8B96A' }}></i>
            </p>
          </div>
        </section>

        {/* Grid */}
        <section id="jacket-grid" className="py-20" style={{ backgroundColor: '#FAF7F2' }}>
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <RevealDiv className="text-center mb-12">
              <p style={{ fontFamily: '"Dancing Script", cursive', color: '#C9933A', fontSize: '1.2rem' }}>Handpicked for every goddess</p>
              <h2 style={{ fontFamily: '"Playfair Display", serif', color: '#4A3728' }} className="text-4xl font-bold mt-1">Our Jacket Collection</h2>
            </RevealDiv>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7" data-product-shop>
              {jackets.map((j, i) => (
                <RevealDiv key={j.id} style={{ transitionDelay: `${i * 0.08}s` }}>
                  <div className="rounded-2xl overflow-hidden group cursor-pointer" style={{ backgroundColor: '#fff' }}>
                    <div className="overflow-hidden" style={{ height: '340px' }}>
                      <img src={j.img} alt={j.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-xs font-bold uppercase tracking-wide px-2 py-1 rounded-full" style={{ backgroundColor: '#EDE5D8', color: '#C9933A', fontFamily: '"Lato", sans-serif' }}>{j.type}</span>
                        <span className="text-xs font-bold uppercase tracking-wide px-2 py-1 rounded-full" style={{ backgroundColor: '#1E4F4F', color: '#E8B96A', fontFamily: '"Lato", sans-serif' }}>{j.sizes}</span>
                      </div>
                      <h3 style={{ fontFamily: '"Playfair Display", serif', color: '#4A3728' }} className="text-lg font-bold mb-1">{j.title}</h3>
                      <p style={{ fontFamily: '"Lato", sans-serif', color: '#C9933A', fontWeight: 700 }} className="text-sm mb-3">{j.price}</p>
                      <a href="mailto:earthwisdom80@gmail.com" className="inline-flex items-center gap-1.5 text-sm font-bold whitespace-nowrap" style={{ color: '#C9933A', fontFamily: '"Lato", sans-serif' }}>
                        Inquire for Availability <i className="ri-arrow-right-line"></i>
                      </a>
                    </div>
                  </div>
                </RevealDiv>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <RevealDiv>
          <section className="py-16" style={{ backgroundColor: '#1E4F4F' }}>
            <div className="max-w-3xl mx-auto px-4 text-center">
              <h2 style={{ fontFamily: '"Playfair Display", serif', color: '#FAF7F2' }} className="text-3xl font-bold mb-4">Find Your Perfect Jacket</h2>
              <p style={{ fontFamily: '"Lato", sans-serif', color: '#A0C8C8', lineHeight: 1.8 }} className="text-base mb-8">
                Call or email us to check current availability, discuss sizing, and place your order. We love helping you find the perfect piece.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:7346468620" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg font-bold uppercase tracking-wide whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5" style={{ backgroundColor: '#C9933A', color: '#fff', fontFamily: '"Lato", sans-serif' }}>
                  <i className="ri-phone-line"></i> 734.646.8620
                </a>
                <a href="mailto:earthwisdom80@gmail.com" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg font-bold uppercase tracking-wide whitespace-nowrap border-2 border-[#E8B96A] transition-all duration-200 hover:-translate-y-0.5" style={{ color: '#E8B96A', fontFamily: '"Lato", sans-serif', backgroundColor: 'transparent' }}>
                  <i className="ri-mail-line"></i> Email Us
                </a>
              </div>
            </div>
          </section>
        </RevealDiv>
      </main>
      <Footer />
    </div>
  );
}
